import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Persistent browser session id
function getSessionId() {
  let sid = localStorage.getItem('tv_session_id');
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('tv_session_id', sid);
  }
  return sid;
}
export const sessionId = getSessionId();

const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  config.headers['X-Session-Id'] = sessionId;
  const t = localStorage.getItem('tv_auth_token');
  if (t) config.headers['Authorization'] = `Bearer ${t}`;
  return config;
});

// Pushes
export async function createPush(payload) {
  const { data } = await client.post('/pushes', payload);
  return data;
}
export async function getPushPreview(token) {
  const { data } = await client.get(`/pushes/${token}/preview`);
  return data;
}
export async function revealPush(token, passphrase, revealToken) {
  const body = { passphrase: passphrase || null };
  if (revealToken) body.reveal_token = revealToken;
  const { data } = await client.post(`/pushes/${token}/reveal`, body);
  return data;
}
export async function deletePush(token) {
  const { data } = await client.delete(`/pushes/${token}`);
  return data;
}
export async function listMyPushes() {
  const { data } = await client.get(`/pushes/session/me`);
  return data;
}

// Auth
export async function apiRegister(body) {
  const { data } = await client.post('/auth/register', body);
  return data;
}
export async function apiLogin(body) {
  const { data } = await client.post('/auth/login', body);
  return data;
}
export async function apiRequestPasswordReset(email) {
  const { data } = await client.post('/auth/password/request-reset', { email });
  return data;
}
export async function apiConfirmPasswordReset(email, code, newPassword) {
  const { data } = await client.post('/auth/password/reset', { email, code, new_password: newPassword });
  return data;
}
export async function apiMe() {
  const { data } = await client.get('/auth/me');
  return data;
}

// Brand
export async function getMyBrand() {
  const { data } = await client.get('/brands/me');
  return data;
}
export async function updateMyBrand(body) {
  const { data } = await client.patch('/brands/me', body);
  return data;
}
export async function subscribeMock(plan) {
  const { data } = await client.post('/brands/me/subscribe', { plan });
  return data;
}
export async function getPublicBrand(slug) {
  const { data } = await client.get(`/brands/public/${slug}`);
  return data;
}
export async function getPlans() {
  const { data } = await client.get('/plans');
  return data;
}
export async function notifyPurchaseInterest(plan, extra = {}) {
  const { data } = await client.post('/subscriptions/notify-interest', { plan, ...extra });
  return data;
}
export async function getBrandStats() {
  const { data } = await client.get('/brands/me/stats');
  return data;
}

// Custom domain
export async function setCustomDomain(domain) {
  const { data } = await client.post('/brands/me/domain', { domain });
  return data;
}
export async function removeCustomDomain() {
  const { data } = await client.delete('/brands/me/domain');
  return data;
}
export async function getDomainInstructions() {
  const { data } = await client.get('/brands/me/domain/instructions');
  return data;
}
export async function verifyCustomDomain() {
  const { data } = await client.post('/brands/me/domain/verify');
  return data;
}

// Reseller pushes (all pushes owned by authenticated user)
export async function listBrandPushes() {
  const { data } = await client.get('/pushes/brand/me');
  return data;
}

// Public brand by hostname (for custom domain)
export async function getBrandByHost(host) {
  const { data } = await client.get(`/brands/by-host/${encodeURIComponent(host)}`);
  return data;
}

// Admin
export async function adminStats() {
  const { data } = await client.get('/admin/stats');
  return data;
}
export async function adminListUsers(q) {
  const { data } = await client.get('/admin/users', { params: q ? { q } : {} });
  return data;
}
export async function adminListBrands(plan) {
  const { data } = await client.get('/admin/brands', { params: plan ? { plan } : {} });
  return data;
}
export async function adminListSubscriptions() {
  const { data } = await client.get('/admin/subscriptions');
  return data;
}
export async function adminCreateUser(body) {
  const { data } = await client.post('/admin/users', body);
  return data;
}
export async function adminDeleteUser(id) {
  const { data } = await client.delete(`/admin/users/${id}`);
  return data;
}
export async function adminResetPassword(id, new_password) {
  const { data } = await client.post(`/admin/users/${id}/reset-password`, { new_password });
  return data;
}
export async function adminBlockUser(id) {
  const { data } = await client.post(`/admin/users/${id}/block`);
  return data;
}
export async function adminUnblockUser(id) {
  const { data } = await client.post(`/admin/users/${id}/unblock`);
  return data;
}
export async function adminDeleteBrand(id) {
  const { data } = await client.delete(`/admin/brands/${id}`);
  return data;
}
export async function adminChangePlan(brandId, plan) {
  const { data } = await client.post(`/admin/brands/${brandId}/plan`, { plan });
  return data;
}
export async function adminGetSettings() {
  const { data } = await client.get('/admin/settings');
  return data;
}
export async function adminUpdateSettings(body) {
  const { data } = await client.patch('/admin/settings', body);
  return data;
}
export async function adminTestM365() {
  const { data } = await client.post('/admin/settings/m365/test');
  return data;
}

// Logo upload
export async function uploadBrandLogo(file) {
  const fd = new FormData();
  fd.append('logo', file);
  const { data } = await client.post('/brands/me/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

// File push upload (large files → GridFS)
export async function createFilePush(payload) {
  const fd = new FormData();
  fd.append('file', payload.file);
  fd.append('expire_days', payload.expire_days);
  fd.append('expire_views', payload.expire_views);
  if (payload.passphrase) fd.append('passphrase', payload.passphrase);
  fd.append('deletable_by_viewer', payload.deletable_by_viewer ? 'true' : 'false');
  if (payload.note) fd.append('note', payload.note);
  if (payload.brand_slug) fd.append('brand_slug', payload.brand_slug);
  if (payload.email_recipients && payload.email_recipients.length) {
    fd.append('email_recipients', JSON.stringify(payload.email_recipients));
  }
  const { data } = await client.post('/pushes/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: payload.onProgress,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });
  return data;
}

// Email OTP for push
export async function requestPushEmailCode(token, email) {
  const { data } = await client.post(`/pushes/${token}/request-code`, { email });
  return data;
}
export async function verifyPushEmailCode(token, email, code) {
  const { data } = await client.post(`/pushes/${token}/verify-code`, { email, code });
  return data;
}
export async function downloadPushFile(token, revealToken) {
  const resp = await client.get(`/pushes/${token}/file`, {
    params: { reveal_token: revealToken },
    responseType: 'blob',
  });
  return resp;
}

// PayPal (MOCK)
export async function paypalCreateOrder(plan) {
  const { data } = await client.post('/paypal/create-order', { plan });
  return data;
}
export async function paypalCapture(orderId) {
  const { data } = await client.post(`/paypal/capture/${orderId}`);
  return data;
}
